import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SeoHead from '../seo/SeoHead';
import {
    MdCarRepair,
    MdEngineering,
    MdSpeed,
    MdVerified,
    MdLocationOn,
    MdPhone,
    MdWhatsapp,
    MdStar,
    MdAccessTime,
    MdPrecisionManufacturing,
    MdElectricCar
} from 'react-icons/md';
import { FaTools, FaShieldAlt } from 'react-icons/fa';
import { GiCarWheel } from 'react-icons/gi';
import CarRepairHeroImg from '../../assets/service-car-repair-hero.jpg';

const CarRepairService = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleWhatsAppClick = () => {
        const phoneNumber = '971561868236';
        const message = encodeURIComponent(t('Hello, I would like to inquire about your car repair services.'));
        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    };

    const services = [
        {
            icon: MdEngineering,
            title: t('Engine Diagnostics & Repair'),
            description: t('Complete engine analysis and professional repair services')
        },
        {
            icon: GiCarWheel,
            title: t('Brake System Service'),
            description: t('Brake pad replacement, disc resurfacing, and brake fluid service')
        },
        {
            icon: MdSpeed,
            title: t('A/C Gas Refill'),
            description: t('Air conditioning gas refill and cooling system inspection')
        },
        {
            icon: MdPrecisionManufacturing,
            title: t('Transmission Service'),
            description: t('Automatic and manual transmission repair and maintenance')
        },
        {
            icon: MdElectricCar,
            title: t('Electrical System Repair'),
            description: t('Battery, alternator, starter, and electrical component diagnosis')
        },
        {
            icon: FaTools,
            title: t('Oil Change'),
            description: t('High-quality oil and filter change for optimal engine health')
        }
    ];

    const features = [
        {
            icon: MdVerified,
            title: t('Certified Technicians'),
            description: t('ASE certified and experienced automotive professionals')
        },
        {
            icon: FaShieldAlt,
            title: t('Quality Guarantee'),
            description: t('All repairs backed by comprehensive warranty coverage')
        },
        {
            icon: MdAccessTime,
            title: t('Quick Turnaround'),
            description: t('Fast and efficient service to get you back on the road')
        },
        {
            icon: MdLocationOn,
            title: t('Convenient Location'),
            description: t('Easily accessible location in Abu Dhabi with ample parking')
        }
    ];

    return (
        <>
            <SeoHead
                path="services/car-repair-service"
                title={t('Car Repair Abu Dhabi | Alprofessor Professional Automotive Service')}
                description={t('Professional car repair services in Abu Dhabi by Alprofessor. Expert automotive diagnostics, engine repair, brake service, and maintenance. Call +971561868236')}
                keywords={t('car repair Abu Dhabi, Alprofessor car repair, automotive service Abu Dhabi, car maintenance, engine repair, brake service, transmission repair, car diagnostics Abu Dhabi')}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "AutoRepair",
                    "name": "Alprofessor Car Repair",
                    "description": t("Professional car repair services in Abu Dhabi"),
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Abu Dhabi",
                        "addressCountry": "AE"
                    },
                    "telephone": "+971561868236",
                    "priceRange": "$$",
                    "areaServed": ["Abu Dhabi", "Dubai", "United Arab Emirates"]
                }}
            />

            <div className={`min-h-screen bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-[#1a3175] via-[#123170] to-[#02a8dd] text-white py-20">
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="flex justify-center mb-6">
                                <div className="bg-white/10 backdrop-blur p-4 rounded-full">
                                    <MdCarRepair className="w-16 h-16 text-white" />
                                </div>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                                {t('Professional Car Repair in Abu Dhabi')}
                            </h1>
                            <p className="text-xl md:text-2xl mb-8 text-blue-100">
                                {t('Alprofessor - Your Trusted Automotive Service Partner')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={handleWhatsAppClick}
                                    className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105"
                                >
                                    <MdWhatsapp className="w-6 h-6" />
                                    {t('Contact Us on WhatsApp')}
                                </button>
                                <a
                                    href="tel:+971561868236"
                                    className="bg-white/10 backdrop-blur border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300"
                                    dir="ltr"
                                >
                                    <MdPhone className="w-6 h-6" />
                                    +971 56 186 8236
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Image Showcase */}
                <section className="py-12 bg-white">
                    <div className="container mx-auto px-4">
                        <img
                            src={CarRepairHeroImg}
                            alt={t('Professional Car Repair in Abu Dhabi')}
                            className="w-full max-w-5xl mx-auto rounded-2xl shadow-xl"
                            loading="lazy"
                        />
                    </div>
                </section>

                {/* Services Section */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                {t('Our Car Repair Services')}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                {t('Alprofessor offers comprehensive automotive repair services in Abu Dhabi with state-of-the-art equipment and expert technicians.')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {services.map((service, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 border border-gray-100">
                                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                                        <service.icon className="w-8 h-8 text-blue-600" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600">
                                        {service.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why Choose Us Section */}
                <section className="py-16 bg-gray-100">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                {t('Why Choose Alprofessor Car Repair?')}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                {t('We are Abu Dhabi\'s premier automotive service center, committed to excellence and customer satisfaction.')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="text-center bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                                    <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <feature.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                {t('What Our Customers Say')}
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[1, 2, 3].map((item) => (
                                <div key={item} className="bg-gray-50 rounded-lg p-6 shadow-md">
                                    <div className="flex items-center mb-4">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <MdStar key={star} className="w-5 h-5 text-yellow-400" />
                                        ))}
                                    </div>
                                    <p className="text-gray-600 mb-4 italic">
                                        "{t('Excellent service and professional staff. Alprofessor fixed my car quickly and at a fair price. Highly recommended!')}"
                                    </p>
                                    <p className="font-semibold text-gray-900">
                                        {t('Satisfied Customer')}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16 bg-gradient-to-r from-[#02a8dd] to-[#1a3175] text-white">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            {t('Need Car Repair Services in Abu Dhabi?')}
                        </h2>
                        <p className="text-xl mb-8 text-blue-100">
                            {t('Contact Alprofessor today for professional automotive repair and maintenance services.')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <button
                                onClick={handleWhatsAppClick}
                                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
                            >
                                <MdWhatsapp className="w-6 h-6" />
                                {t('WhatsApp: +971 56 186 8236')}
                            </button>

                            <div className="flex items-center gap-2 text-blue-100">
                                <MdLocationOn className="w-5 h-5" />
                                <span>{t('Abu Dhabi, UAE')}</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default CarRepairService;